import axios, { AxiosRequestHeaders, AxiosResponse } from "axios";
import { IdTokenResult } from "firebase/auth";
import { useState } from "react";
import { auth } from "../../config/firebaseConfig";

import {
  AxiosHookType,
  DefaultResponseType,
  AxiosResponseType,
} from "../../types/axiosTypes";

// Zonder timeout blijft een request op een vastgelopen backend eeuwig
// "pending" en komt de retry-keten in useUserRoutes nooit op gang — dat was
// de resterende bron van de eindeloze "Applicatie laden"-loop. Ruim genoeg
// voor de zwaarste calls (PDF/Excel-export).
const REQUEST_TIMEOUT_MS = 20000;

export default function useAxios({
  method,
  body = null,
}: AxiosHookType<any>): AxiosResponseType {
  const [_error, setError] = useState<string | null>(null);
  const [loading, setloading] = useState<boolean>(false);

  const fetchData = async (
    url: string,
    newBody?: any
  ): Promise<DefaultResponseType<any>> => {
    try {
      setloading(true);

      const token: IdTokenResult | undefined =
        await auth.currentUser?.getIdTokenResult();

      const headers: AxiosRequestHeaders = {
        "Content-Type": "application/json",
        authorization: `Bearer ${token?.token}`,
      };

      axios.defaults.withCredentials = true;

      const requestBody = newBody !== undefined ? newBody : body;

      const { data: response }: AxiosResponse<DefaultResponseType<any>> =
        await axios({
          method,
          url,
          timeout: REQUEST_TIMEOUT_MS,
          ...(requestBody !== null && requestBody !== undefined
            ? { data: requestBody }
            : {}),
          headers,
        });

      setloading(false);

      return response;
    } catch (e) {
      setloading(false);

      if (axios.isAxiosError(e)) {
        // Status behouden zodat callers 401/403 van netwerk-/timeoutfouten
        // kunnen onderscheiden; een timeout heeft géén response en houdt dus
        // statusCode undefined → wordt als transient behandeld.
        const status = e.response?.status;
        const serverData = e.response?.data;
        setError(e.message);
        return {
          data: serverData ?? {},
          count: 0,
          error: true,
          statusCode: status,
          message: e.message,
        };
      }

      if (e instanceof Error) {
        setError(e.message);
        return {
          data: {},
          count: 0,
          error: true,
          message: e.message,
        };
      }

      return {
        data: {},
        count: 0,
        error: true,
        message: "Unknown error",
      };
    }
  };

  return { error: _error, loading, fetchData };
}
